import React, { useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import API from '../config/api';
import IMAGES from '../config/images';
import logowhite from '../assets/logowhite.png';
import './Login.css';

const Login = () => {
  const [isLoginView, setIsLoginView] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login } = useContext(AuthContext);
  const { lang, t } = useLanguage();
  const navigate = useNavigate();

  const handleAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const endpoint = isLoginView ? API.auth.login : API.auth.register;
    const bodyData = isLoginView 
      ? { email, password } 
      : { name, email, password, phone_number: phone, buyer_type: 'retail' };

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyData),
      });
      const data = await response.json();

      if (response.ok && data.status === 'success') {
        if (isLoginView) {
          login(data.user, data.jwt);
          navigate('/');
        } else {
          alert('Registration successful! Please login.');
          setIsLoginView(true);
        }
      } else {
        setError(data.message || 'Authentication failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <section className="auth-brand-panel" aria-hidden="true">
        <div className="auth-brand-bg" style={{ backgroundImage: `url("${IMAGES.heroLogin}")` }}></div>
        <div className="auth-brand-overlay"></div>
        <div className="auth-brand-content animate-in">
          <div className="auth-logo-circle">
            <img src={logowhite} alt="Kaaleen Haus" className="auth-logo-white" />
          </div>
          <p className="auth-brand-kicker">{lang === 'de' ? t('login.brandKicker') : 'Curated Heritage Rugs'}</p>
          <h1 className="auth-brand-title">{lang === 'de' ? t('login.brandTitle') : 'Welcome to Kaaleen Haus'}</h1>
          <p className="auth-brand-copy">
            {lang === 'de' ? t('login.brandDesc') : 'Where centuries of tradition weave into modern luxury. Sign in to access your account, trade pricing, and exclusive collections.'}
          </p>
        </div>
      </section>

      <section className="auth-form-panel">
        <div className="auth-card animate-in">
          <div className="auth-tabs" role="tablist" aria-label="Authentication options">
            <button
              type="button"
              onClick={() => setIsLoginView(true)}
              className={`auth-tab ${isLoginView ? 'active' : ''}`}
            >
              {lang === 'de' ? t('login.tabs.signIn') : 'Sign In'}
            </button>
            <button
              type="button"
              onClick={() => setIsLoginView(false)}
              className={`auth-tab ${!isLoginView ? 'active' : ''}`}
            >
              {lang === 'de' ? t('login.tabs.register') : 'Register'}
            </button>
          </div>

          <p className="auth-subheading">
            {isLoginView
              ? (lang === 'de' ? t('login.subheadings.signIn') : 'Continue to your account and track your orders.')
              : (lang === 'de' ? t('login.subheadings.register') : 'Create your profile to save favorites and faster checkout.')}
          </p>

          <form onSubmit={handleAuth} className="auth-form">
            {!isLoginView && (
              <>
                <div className="auth-field">
                  <label htmlFor="auth-name">{lang === 'de' ? t('login.fields.name') : 'Full Name'}</label>
                  <input
                    id="auth-name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    className="auth-input"
                    placeholder={lang === 'de' ? t('login.placeholders.name') : 'Your full name'}
                    autoComplete="name"
                  />
                </div>

                <div className="auth-field">
                  <label htmlFor="auth-phone">{lang === 'de' ? t('login.fields.phone') : 'Phone Number'}</label>
                  <input
                    id="auth-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="auth-input"
                    placeholder="+91"
                    autoComplete="tel"
                  />
                </div>
              </>
            )}

            <div className="auth-field">
              <label htmlFor="auth-email">{lang === 'de' ? t('login.fields.email') : 'Email Address'}</label>
              <input
                id="auth-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="auth-input"
                placeholder={lang === 'de' ? t('login.placeholders.email') : 'you@example.com'}
                autoComplete="email"
              />
            </div>

            <div className="auth-field">
              <label htmlFor="auth-password">{lang === 'de' ? t('login.fields.password') : 'Password'}</label>
              <input
                id="auth-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="auth-input"
                placeholder="••••••••"
                autoComplete={isLoginView ? 'current-password' : 'new-password'}
              />
            </div>

            {error && <p className="auth-error">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary auth-submit-btn"
            >
              {loading ? (lang === 'de' ? t('login.buttons.processing') : 'Processing...') : isLoginView ? (lang === 'de' ? t('login.buttons.signIn') : 'Sign In') : (lang === 'de' ? t('login.buttons.register') : 'Create Account')}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Login;
